import { Plus } from 'lucide-react'
import { WorkshopSection } from './WorkshopSection'
import { BlockCard } from './BlockCard'
import { blockSlotsForGroup } from './blockSlots'
import { useFocusedBlock } from './FocusedBlockContext'
import { useWorkshopMode } from './WorkshopModeContext'
import { useComposerStore, type BlockCategory } from '@/stores/composerStore'
import type { BlockGroup } from './blockTaxonomy'

interface Props {
  group: BlockGroup
  emoji: string
  title: string
  subtitle: string
  name: string
  starters?: BlockCategory[]
  addable?: boolean          // false → never show "+ Add block" (e.g. 'who')
  defaultOpen?: boolean
  onAddBlock: (group: BlockGroup) => void
}

/** Card-grid section for one taxonomy group. Existing blocks render in any mode;
 *  add/remove is Advanced-only. Advanced-tier groups hide entirely when empty. */
export function BlockSection({ group, emoji, title, subtitle, name, starters = [], addable = true, defaultOpen, onAddBlock }: Props) {
  const { mode } = useWorkshopMode()
  const base = useComposerStore((s) => s.draftBundle ?? s.serverBundle)
  const removeBlockAt = useComposerStore((s) => s.removeBlockAt)
  const moveBlock = useComposerStore((s) => s.moveBlock)
  const { open, target, revealTarget, clearReveal } = useFocusedBlock()
  const slots = blockSlotsForGroup(base, group, starters)

  const advancedTier = group === 'rules' || group === 'know' || group === 'extra'
  if (advancedTier && slots.length === 0 && mode !== 'advanced') return null

  // A just-added block reveals in its destination section, but only once the
  // focus editor has closed (target === null) so the scroll is actually visible.
  const revealHere = revealTarget?.group === group
  return (
    <WorkshopSection
      emoji={emoji}
      title={title}
      subtitle={subtitle}
      defaultOpen={defaultOpen ?? (!advancedTier || slots.length > 0)}
      forceOpen={revealHere}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {slots.map((s, i) => (
          <BlockCard
            key={`${s.category}:${s.ordinal}`}
            category={s.category}
            index={s.index}
            ordinal={s.ordinal}
            name={name}
            reveal={
              target === null &&
              revealHere &&
              revealTarget?.category === s.category &&
              revealTarget?.ordinal === s.ordinal
            }
            onRevealComplete={clearReveal}
            onOpen={() => open({ index: s.index, category: s.category, ordinal: s.ordinal })}
            onRemove={s.index !== null ? () => removeBlockAt(s.index as number) : undefined}
            onMoveUp={
              s.index !== null && i > 0 && slots[i - 1].index !== null
                ? () => moveBlock(s.index as number, slots[i - 1].index as number)
                : undefined
            }
            onMoveDown={
              s.index !== null && i < slots.length - 1 && slots[i + 1].index !== null
                ? () => moveBlock(s.index as number, slots[i + 1].index as number)
                : undefined
            }
          />
        ))}
        {addable && mode === 'advanced' && (
          <button
            type="button"
            onClick={() => onAddBlock(group)}
            className="flex min-h-[128px] flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-border bg-transparent p-3.5 text-sm font-semibold text-muted-foreground transition hover:border-primary hover:bg-primary/10 hover:text-primary"
          >
            <Plus className="h-5 w-5" aria-hidden /> Add block
          </button>
        )}
      </div>
    </WorkshopSection>
  )
}
